// The vocabulary the UI shows, and the order it shows it in.
//
// The roles and topics themselves come from the city file's `taxonomy`; the
// tables here are the contract's copy (`handoff/demo/contract.md` §1), for
// tests and for anything drawn before the file arrives. The rest is
// presentation: weekdays, time slots, switches, labels and formatting.

import {
  endMinute,
  hasEndTime,
  startMinute,
  type Commitment,
  type Opportunity,
  type VocabularyEntry,
} from './opportunity';

// --- roles and topics --------------------------------------------------------

/** "I want to be a…": exactly these six. */
export const ROLES: VocabularyEntry[] = [
  { key: 'green-thumb', label: 'Green Thumb', subtext: 'I want to get my hands dirty', icon: '🌱', sortOrder: 1 },
  { key: 'steward', label: 'Steward', subtext: 'I want to heal my local ecosystem', icon: '🌊', sortOrder: 2 },
  { key: 'scientist', label: 'Scientist', subtext: 'I want to gather hyperlocal climate data', icon: '🔬', sortOrder: 3 },
  { key: 'advocate', label: 'Advocate', subtext: "I want to stand up for what's right", icon: '📣', sortOrder: 4 },
  { key: 'student', label: 'Student', subtext: "I want to understand what's happening", icon: '📚', sortOrder: 5 },
  { key: 'good-neighbor', label: 'Good Neighbor', subtext: 'I want to meet people who care', icon: '🤝', sortOrder: 6 },
];

/** "I'm interested in…": exactly these ten. */
export const TOPICS: VocabularyEntry[] = [
  { key: 'nature', label: 'Nature & Biodiversity', icon: '🦋', sortOrder: 1 },
  { key: 'water', label: 'Water & Waterfronts', icon: '💧', sortOrder: 2 },
  { key: 'food', label: 'Food & Agriculture', icon: '🥬', sortOrder: 3 },
  { key: 'waste', label: 'Waste & Circularity', icon: '♻️', sortOrder: 4 },
  { key: 'energy', label: 'Energy & Buildings', icon: '⚡', sortOrder: 5 },
  { key: 'transport', label: 'Transportation & Streets', icon: '🚲', sortOrder: 6 },
  { key: 'resilience', label: 'Climate Resilience', icon: '🌧️', sortOrder: 7 },
  { key: 'justice', label: 'Environmental Justice', icon: '⚖️', sortOrder: 8 },
  { key: 'health', label: 'Health & Wellbeing', icon: '💚', sortOrder: 9 },
  { key: 'art', label: 'Art & Culture', icon: '🎨', sortOrder: 10 },
];

// --- event-type words ------------------------------------------------------

/** The edit form's Format drop-down: the words the callout may show. */
export const EVENT_TYPES: string[] = [
  'CLEANUP', 'PLANTING', 'WORKSHOP', 'TOUR', 'MEETING', 'HEARING', 'RALLY', 'TALK', 'WORKDAY',
  'VOLUNTEER', 'CITIZEN SCIENCE', 'FESTIVAL', 'SOCIAL', 'TRAINING', 'SCREENING', 'MARKET',
];

// --- commitment --------------------------------------------------------------

export const COMMITMENT_LABEL: Record<Commitment, string> = {
  one_off: 'ONE-OFF',
  weekly: 'WEEKLY',
  ongoing: 'ONGOING',
};

// --- weekdays ----------------------------------------------------------------

export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

/** Monday first. The filter applies to every date in the window. */
export const WEEKDAYS: { key: Weekday; label: string; weekday: string }[] = [
  { key: 'mon', label: 'Mon', weekday: 'Monday' },
  { key: 'tue', label: 'Tue', weekday: 'Tuesday' },
  { key: 'wed', label: 'Wed', weekday: 'Wednesday' },
  { key: 'thu', label: 'Thu', weekday: 'Thursday' },
  { key: 'fri', label: 'Fri', weekday: 'Friday' },
  { key: 'sat', label: 'Sat', weekday: 'Saturday' },
  { key: 'sun', label: 'Sun', weekday: 'Sunday' },
];

const BY_UTC_DAY: Weekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** The weekday of a "2026-09-22" day key. */
export function weekdayOf(dayKey: string): Weekday {
  const [y, m, d] = dayKey.split('-').map(Number);
  return BY_UTC_DAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

// --- time of day -----------------------------------------------------------

export type TimeSlot = 'morning' | 'afternoon' | 'evening' | 'night';

export const TIME_SLOTS: { id: TimeSlot; label: string; hint: string }[] = [
  { id: 'morning', label: 'Morning', hint: 'before 12PM' },
  { id: 'afternoon', label: 'Afternoon', hint: '12PM – 4PM' },
  { id: 'evening', label: 'Evening', hint: '4PM – 7PM' },
  { id: 'night', label: 'Night', hint: '7PM – late' },
];

/**
 * All-day events return null rather than "morning". Many start at 12:00am
 * because the source published no time; bucketing those as morning would hand
 * anyone filtering for morning events a fistful of week-long conferences.
 */
export function timeSlotOf(event: Opportunity): TimeSlot | null {
  const start = startMinute(event);
  if (event.isAllDay || start == null) return null;
  const h = start / 60;
  if (h < 12) return 'morning';
  if (h < 16) return 'afternoon';
  if (h < 19) return 'evening';
  return 'night';
}

// --- switches ------------------------------------------------------------------
//
// Each narrows to records that say yes. A record that doesn't say (null) is
// excluded and counted, never treated as "no" (contract §2).

export type SwitchKey =
  | 'inPerson'
  | 'virtual'
  | 'indoor'
  | 'outdoor'
  | 'free'
  | 'accessible'
  | 'noExperience'
  | 'under18';

export const SWITCHES: { key: SwitchKey; label: string }[] = [
  { key: 'inPerson', label: 'In-Person Only' },
  { key: 'virtual', label: 'Virtual Only' },
  { key: 'indoor', label: 'Indoor' },
  { key: 'outdoor', label: 'Outdoor' },
  { key: 'free', label: 'Free' },
  { key: 'accessible', label: 'Accessible' },
  { key: 'noExperience', label: 'No Experience Needed' },
  { key: "under18", label: "I'm under 18" },
];

// --- formatting ------------------------------------------------------------

export function formatTime(minutes: number | null): string {
  if (minutes == null) return '';
  const h24 = Math.floor(minutes / 60);
  const m = minutes % 60;
  const suffix = h24 < 12 ? 'AM' : 'PM';
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0 ? `${h}:00 ${suffix}` : `${h}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "10:00 AM - 4:00 PM", or just the start when the end was never published. */
export function formatTimeRange(event: Opportunity): string {
  if (event.isAllDay) return 'All day';
  const start = formatTime(startMinute(event));
  if (!hasEndTime(event)) return start;
  return `${start} - ${formatTime(endMinute(event))}`;
}

/** "MANHATTAN — MIDTOWN" */
export function formatArea(area: string | null): string {
  if (!area) return '';
  return area.replace(/\s*-\s*/, ' — ').toUpperCase();
}

export function longDate(dayKey: string): { full: string; weekday: string } {
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return {
    full: date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' }),
    weekday: date.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' }),
  };
}

export function mapsUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
